import { Fragment } from "react";
import { headlineDisplay } from "@/zapi-frontend/shared/lib/figureRefs";
import type { THeadlineBlock, THeadlineItem } from "@/zapi-frontend/shared/types/contentBlocks";
import { BlockLabel } from "./_BlockLabel";
import { useFigureRefs } from "./FigureRefs/FigureRefsProvider";
import { FigureTrigger } from "./FigureRefs/FigureTrigger";
import { memoType } from "./memoTypography";
import { cn } from "@/zapi-frontend/shared/lib/utils";

const MAX_COLUMNS = 4;

// The design shows one to four figures in a row; a block with more is split into rows of
// the same size so a fifth figure never sits alone under four.
export const headlineRows = (items: THeadlineItem[]): THeadlineItem[][] => {
  const rowCount = Math.ceil(items.length / MAX_COLUMNS);
  const perRow = Math.ceil(items.length / rowCount);
  return Array.from({ length: rowCount }, (_, row) => items.slice(row * perRow, (row + 1) * perRow));
};

export function HeadlineBlock({ block }: { block: THeadlineBlock }) {
  const figures = useFigureRefs();
  const rows = headlineRows(block.items);

  return (
    <figure className="@container flex min-w-0 flex-col gap-3" data-testid="headline-block">
      <BlockLabel label={block.label} className="" />
      {rows.map((row, r) => (
        <div key={r} className="grid grid-cols-2 items-start gap-x-4 gap-y-3 @2xl:flex @2xl:gap-4">
          {row.map((item, c) => (
            <Fragment key={c}>
              {c > 0 && <div className="hidden w-px shrink-0 self-stretch bg-innoveraZinc-150 @2xl:block" />}
              <div className="flex min-w-0 flex-1 flex-col gap-1 py-1">
                <div className={cn("wrap-break-word", memoType.figure)}>
                  <FigureTrigger figureKey={item.key}>
                    {headlineDisplay(item, item.key ? figures?.get(item.key) : undefined)}
                  </FigureTrigger>
                </div>
                <div className={memoType.figureLabel}>{item.label}</div>
              </div>
            </Fragment>
          ))}
        </div>
      ))}
    </figure>
  );
}
